// src/components/Projects/Projects.js
import React from "react";
import "./Projects.css";
import Project from "./Project";

// Tu remplaceras ces imports par tes vraies images
import emg_vid from "../../assets/projects/emg/emg-vid.mp4";
import cloud_mobile from "../../assets/projects/cloud/cloud-mobile.png";
import cloud_desktop from "../../assets/projects/cloud/cloud-desktop.png";
import osm_mobile from "../../assets/projects/osm/osm-mobile.png";
import osm_desktop from "../../assets/projects/osm/osm-desktop.png";
import med_agent_mobile from "../../assets/projects/med_agent/med-agent-mobile.png";
import med_agent_desktop from "../../assets/projects/med_agent/med-agent-desktop.png";
import placeholder from "../../assets/images/placeholder.png";

const projectsData = [
  {
  title: "Medical Knowledge Monitoring AI Agent",
  location: "Personal Project · AI for Healthcare",
  dates: "2026",
  description: (
    <>
      <p>
        This project focuses on the design and implementation of an <strong>AI-powered medical
        knowledge monitoring agent</strong> dedicated to official French healthcare publications.
        The objective is to support physicians by providing structured, up-to-date, and
        source-referenced access to institutional medical information, with an initial focus on
        <strong> diabetes-related guidelines and regulations</strong>.
      </p>

      <h4>Main Contributions</h4>
      <ul>
        <li>
          Designed a complete automated monitoring pipeline for official French healthcare sources
          (HAS, ANSM, Assurance Maladie, Ministry of Health).
        </li>
        <li>
          Implemented RSS-based data ingestion to continuously track new recommendations, safety
          alerts, and regulatory updates.
        </li>
        <li>
          Developed document filtering, normalization, and versioning mechanisms to retain only
          diabetes-relevant publications.
        </li>
        <li>
          Built a retrieval-augmented AI agent capable of answering medical queries with explicit
          citations to official sources.
        </li>
      </ul>

      <h4>Medical and Technical Context</h4>
      <ul>
        <li>
          Focused exclusively on <strong>official and authoritative French institutions</strong> to
          ensure reliability and medico-legal acceptability.
        </li>
        <li>
          Addressed challenges related to medical document versioning and change detection across
          guideline updates.
        </li>
        <li>
          Designed strict safety constraints to position the agent as a clinical information
          assistant rather than a diagnostic or decision-making system.
        </li>
        <li>
          Demonstrated how AI can reduce cognitive load for clinicians by transforming large-scale
          regulatory and scientific documentation into actionable knowledge.
        </li>
      </ul>
    </>
  ),
  imageMobile: med_agent_mobile,
  imageDesktop: med_agent_desktop,
  },
  {
    title: "Electromyographic Signal Classification",
    location: "Aalborg University · Research Project",
    dates: "2024",
    description: (
    <>
      <p>
        During an exchange semester at <strong>Aalborg University</strong>, I contributed to a
        research-driven project aiming to classify finger movements from surface EMG (sEMG)
        signals. The work was carried out entirely in English with an international team.
      </p>

      <h4>Main Contributions</h4>
      <ul>
        <li>Conducted a detailed state-of-the-art review on sEMG-based movement classification.</li>
        <li>Designed a full machine learning pipeline (Python, ROS2, LDA, SVM).</li>
        <li>Implemented preprocessing, feature extraction and classification modules.</li>
        <li>Collaborated closely with a supervising professor and a research student.</li>
      </ul>

      <h4>Research Context</h4>
      <ul>
        <li>Used a research-grade EMG acquisition device under confidentiality constraints.</li>
        <li>Worked with sensitive biological data requiring controlled handling.</li>
        <li>
          Demonstrated how AI transforms biological activity into control signals for prosthetics.
        </li>
        <li>Contributed to a research publication.</li>
      </ul>
    </>
  ),
    video: emg_vid,
    imageMobile: placeholder,
    imageDesktop: placeholder,
  },
  {
    title: "Shade-Optimized Pedestrian Routing",
    location: "IMT Atlantique · Student Team Project",
    dates: "2024 – 2025",
    description: (
      <>
        <p>
          This project explores how <strong>pedestrian routing</strong> can be improved by prioritizing
          <strong> shaded paths</strong>, which significantly enhance walking comfort during hot weather.
          Conducted as a team-based student project, it resulted in a concrete contribution to the
          <strong> OpenStreetMap ecosystem</strong> and received positive feedback from the community.
        </p>

        <h4>Main Contributions</h4>
        <ul>
          <li>
            Designed and implemented two complementary approaches to quantify shade along pedestrian
            paths: an <strong>observational method</strong> and a <strong>projection-based method</strong>.
          </li>
          <li>
            Developed a <strong>Python-based geospatial pipeline</strong> to compute shade percentages
            using building heights, tree data and sun position algorithms.
          </li>
          <li>
            Extended <strong>Graphhopper</strong> to integrate numeric shade data
            (<code>shade:percentage</code>) into pedestrian routing profiles.
          </li>
          <li>
            Led the team as <strong>project manager</strong>, using Gantt charts and Kanban boards to
            coordinate tasks and meet deadlines.
          </li>
        </ul>

        <h4>Implementation & Impact</h4>
        <ul>
          <li>
            Demonstrated the system on the city of <strong>Nantes</strong> through a live online routing
            demo comparing standard and shade-optimized routes.
          </li>
          <li>
            Presented the project during a public forum and published detailed posts on both the
            <strong> French and English OpenStreetMap forums</strong>.
          </li>
          <li>
            Collected constructive feedback from the OSM community, validating the relevance of the
            approach for open-source pedestrian routing.
          </li>
          <li>
            Gained hands-on experience in <strong>open-source contribution</strong>, from technical
            implementation to community communication.
          </li>
        </ul>

        <h4>Links</h4>
        <ul>
          <li>
            <a href="https://shaderoutingosm.polyno.me/?point=47.217601%2C-1.556213&point=47.22995%2C-1.554073&profile=shade_percentage_test1&layer=OpenStreetMap" 
            target="_blank" 
            rel="noopener noreferrer">
              Live demo (Nantes)
            </a>
          </li>
          <li>
            <a href="https://github.com/Jihene556/procom_calcul/tree/optimisation" 
            target="_blank" 
            rel="noopener noreferrer">
              GitHub repository for Python shade calculation
            </a> ; 
            <a href="https://github.com/Mehdialaoui02/graphhopper_IMTA"
            target="_blank"
            rel="noopener noreferrer">
              Graphhopper fork with shade routing
            </a>
          </li>
          <li>
            <a href="https://www.openstreetmap.org/user/zorun/diary/406587" 
            target="_blank" 
            rel="noopener noreferrer">
              OpenStreetMap forum post (EN)
            </a> ; 
            <a href="https://forum.openstreetmap.fr/t/calcul-d-itineraires-pietons-ombrages/33385" 
            target="_blank" 
            rel="noopener noreferrer">
              OpenStreetMap forum post (FR)
            </a>
          </li>
        </ul>
      </>
    ),
    imageMobile: osm_mobile,
    imageDesktop: osm_desktop,
  },
  {
    title: "Cloud-Native Voting Application",
    location: "IMT Atlantique · Student Team Project",
    dates: "2025",
    description: (
      <>
        <p>
          In a two-person team using <strong>pair programming</strong>, we deployed a full
          microservices voting application using Docker, Kubernetes, and Terraform. The goal
          was to containerize, orchestrate, and provision the stack locally and on Google Cloud.
        </p>

        <h4>1. Docker – Containerization</h4>
        <ul>
          <li>Created one Dockerfile per service (Python, Node.js, .NET, Redis, PostgreSQL, Nginx).</li>
          <li>Built a complete <strong>docker-compose</strong> stack with front/back networks.</li>
          <li>Applied best practices: slim images, multistage .NET worker, healthchecks, persistent DB volume.</li>
          <li>Configured Nginx as a load balancer.</li>
        </ul>

        <h4>2. Kubernetes – Minikube &amp; GKE</h4>
        <ul>
          <li>Pushed custom images to <strong>GCP Artifact Registry</strong> and Minikube’s Docker runtime.</li>
          <li>Deployed services via Deployments, Services, PV/PVC, and Jobs.</li>
          <li>Implemented liveness probes, init containers for healthchecks, and persistent storage.</li>
          <li>Ran the load generator as a <strong>Kubernetes Job</strong>.</li>
          <li>Explored ConfigMaps and HorizontalPodAutoscaling.</li>
        </ul>

        <h4>3. Terraform – Infrastructure as Code</h4>
        <ul>
          <li>Automated the local Docker stack using the Terraform Docker provider.</li>
          <li>Provisioned a <strong>GKE cluster</strong> and deployed Kubernetes manifests through Terraform.</li>
        </ul>
      </>
    ),
    imageMobile: cloud_mobile,
    imageDesktop: cloud_desktop,
  },
  /* {
    title: "Application de gestion de tâches",
    location: "Freelance",
    dates: "2023",
    description:
      "Application de to-do list avec filtres, persistance des données et design épuré, développée pour un client indépendant.",
    image: project3Img,
  }, */
];

const Projects = () => {
  return (
    <section id="projects" className="projects-section">
      <h2 className="projects-heading">Projects</h2>
      <p className="projects-intro">
        Here is a selection of projects I have worked on recently.
      </p>

      <div className="projects-track">
        {projectsData.map((project, index) => (
          <Project
            key={project.title}
            {...project}
            isReversed={index % 2 === 1} // alterne gauche/droite sur desktop
          />
        ))}
      </div>
    </section>
  );
};

export default Projects;